import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MotionConfig } from 'framer-motion';
import * as Sentry from '@sentry/react';
import { ScenarioInput } from 'src/graphql/_generated';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { RefreshSvg } from 'src/assets/dynamicSvg';
import { useRiskIntelGate } from './useRiskIntelGate';
import { GateStates } from './steps/GateStates';
import { RiskIntelProvider } from './RiskIntelProvider';
import { useRiskIntel } from './useRiskIntel';
import { useScrollOnReveal, scrollWizardToTop } from './useScrollOnReveal';
import { NarrativeStep } from './steps/NarrativeStep';
import { FrequencyStep } from './steps/FrequencyStep';
import { ConsequencesStep } from './steps/ConsequencesStep';
import { ReviewStep } from './steps/ReviewStep';
import { LoaderDots } from './comps/FetchingState';

/** Resolves true on success (host closes the modal), false on error (review stays open). */
export type OnCreateScenario = (data: Partial<ScenarioInput>) => Promise<boolean>;

const Container = styled.div`
  width: 100%;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  min-height: 28px;
  margin-bottom: 16px;
`;

const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.cflowerBlue};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
`;

const ResumeLoading = styled.div`
  display: flex;
  justify-content: center;
  padding: 64px 0;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 56px 24px;
  text-align: center;
`;

const noopCreate: OnCreateScenario = async () => false;

const RiskIntelWizard: React.FC<{ workspaceId?: string; onCreateScenario: OnCreateScenario }> = ({
  workspaceId,
  onCreateScenario,
}) => {
  const { session, resuming, error, reset, timedOut, retrySession } = useRiskIntel();
  const [confirmingReset, setConfirmingReset] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll each lane into view as it unlocks; the async frequency lane gets a second push on data arrival.
  const hasFrequency = !!session?.selectedCycleId;
  // Gate the consequences lane on the chosen frequency OR the impacts-phase status (Edge E3) — a resume
  // that lands past frequency without chosenTimes/Years on the wire would otherwise strand the user.
  const hasConsequences =
    session?.frequency?.chosenValue != null ||
    session?.status === 'mining_impacts' ||
    session?.status === 'awaiting_impacts' ||
    session?.status === 'quantified';
  const frequencyRef = useScrollOnReveal(hasFrequency ? (session?.frequency ? 'loaded' : 'loading') : false);
  const consequencesRef = useScrollOnReveal(hasConsequences);

  // Review replaces the narrative→frequency→consequences stack rather than appending below it.
  const inReview = session?.status === 'review';

  // Entering or leaving review jumps the modal to the top so the tab bar is visible.
  const prevStatusRef = useRef(session?.status);
  useEffect(() => {
    const status = session?.status;
    if (status === 'review' || prevStatusRef.current === 'review') scrollWizardToTop(containerRef.current);
    prevStatusRef.current = status;
  }, [session?.status]);

  // reset() abandons the session and clears the resume pointer, returning to a blank narrative.
  const startOver = () => {
    reset();
    setConfirmingReset(false);
  };

  return (
    <Container ref={containerRef}>
      {session && (
        <TopBar>
          {confirmingReset ? (
            <>
              <Poppins px={14} color="cflowerBlue">
                Discard this scenario and start over?
              </Poppins>
              <TextButton type="button" onClick={startOver} data-cy="ai-start-over-confirm">
                <RefreshSvg size={16} />
                Start over
              </TextButton>
              <TextButton type="button" onClick={() => setConfirmingReset(false)}>
                Cancel
              </TextButton>
            </>
          ) : (
            <TextButton type="button" onClick={() => setConfirmingReset(true)} data-cy="ai-start-over">
              <RefreshSvg size={16} />
              Start over
            </TextButton>
          )}
        </TopBar>
      )}
      {resuming && !session ? (
        <ResumeLoading>
          <LoaderDots />
        </ResumeLoading>
      ) : session?.status === 'errored' ? (
        <ErrorState>
          <Poppins px={18} weight={600} color="prussianBlue">
            Something went wrong
          </Poppins>
          <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
            The risk analysis ran into a problem. Use “Start over” above to try again.
          </Poppins>
        </ErrorState>
      ) : timedOut ? (
        <ErrorState>
          <Poppins px={18} weight={600} color="prussianBlue">
            This is taking longer than expected
          </Poppins>
          <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
            We haven’t heard back from the analysis yet. Check again, or use “Start over” above.
          </Poppins>
          <Spacer $px={8} />
          <TextButton type="button" onClick={retrySession} data-cy="ai-retry">
            <RefreshSvg size={16} />
            Try again
          </TextButton>
        </ErrorState>
      ) : inReview ? (
        <ReviewStep onCreateScenario={onCreateScenario} />
      ) : (
        <>
          <NarrativeStep workspaceId={workspaceId} />
          {hasFrequency && (
            <div ref={frequencyRef}>
              <Spacer $px={32} />
              <FrequencyStep />
            </div>
          )}
          {hasConsequences && (
            <div ref={consequencesRef}>
              <Spacer $px={32} />
              <ConsequencesStep />
            </div>
          )}
        </>
      )}
      {error && (
        <>
          <Spacer $px={16} />
          <Poppins px={14} color="error" css="text-align: center; display: block;">
            {error}
          </Poppins>
        </>
      )}
    </Container>
  );
};

interface RiskIntelTabProps {
  assessmentId: string;
  assessmentName?: string;
  workspaceId?: string;
  onCreateScenario?: OnCreateScenario;
}

export const RiskIntelTab: React.FC<RiskIntelTabProps> = ({
  assessmentId,
  assessmentName,
  workspaceId,
  onCreateScenario = noopCreate,
}) => {
  const gate = useRiskIntelGate();

  // A render error degrades to the unavailable gate visual, keeping the rest of the modal usable.
  return (
    <MotionConfig reducedMotion="user">
      <Sentry.ErrorBoundary fallback={<GateStates state="unavailable" />}>
        {gate === 'enabled' ? (
          <RiskIntelProvider assessmentId={assessmentId} assessmentName={assessmentName}>
            <RiskIntelWizard workspaceId={workspaceId} onCreateScenario={onCreateScenario} />
          </RiskIntelProvider>
        ) : (
          <GateStates state={gate} />
        )}
      </Sentry.ErrorBoundary>
    </MotionConfig>
  );
};
